import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Eye, EyeOff, Loader2, Mail, Megaphone, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MarketingTab = ({ token }) => {
  const [subTab, setSubTab] = useState('banner');
  const [banner, setBanner] = useState({ enabled: true, text: '', bg_color: '#111', text_color: '#ffffff' });
  const [popup, setPopup] = useState({ enabled: true, headline: 'GET UP TO', discount: '15% OFF', subtext: 'Sign up for exclusive deals, new drops & your discount code.', delay_seconds: 2 });
  const [subscribers, setSubscribers] = useState([]);
  const [subTotal, setSubTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/admin/banner`, { headers: { Authorization: `Bearer ${token}` } }),
      axios.get(`${API}/admin/email-subscribers`, { headers: { Authorization: `Bearer ${token}` } }),
      axios.get(`${API}/admin/popup-banner`, { headers: { Authorization: `Bearer ${token}` } })
    ]).then(([bRes, sRes, pRes]) => {
      setBanner(bRes.data);
      setSubscribers(sRes.data.subscribers);
      setSubTotal(sRes.data.total);
      setPopup(pRes.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [token]);

  const handleSaveBanner = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/banner`, banner, { headers: { Authorization: `Bearer ${token}` } });
      setBanner(res.data);
      toast.success('Banner updated! Refresh the site to see changes.');
    } catch { toast.error('Failed to save banner'); }
    finally { setSaving(false); }
  };

  const handleRemoveBanner = async () => {
    setSaving(true);
    try {
      await axios.delete(`${API}/admin/banner`, { headers: { Authorization: `Bearer ${token}` } });
      setBanner({ enabled: false, text: '', bg_color: '#111', text_color: '#ffffff' });
      toast.success('Banner removed');
    } catch { toast.error('Failed to remove banner'); }
    finally { setSaving(false); }
  };

  const handleDeleteSubscriber = async (id) => {
    try {
      await axios.delete(`${API}/admin/email-subscribers/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setSubscribers(prev => prev.filter(s => s.id !== id));
      setSubTotal(prev => prev - 1);
      toast.success('Subscriber removed');
    } catch { toast.error('Failed to remove'); }
  };

  const handleExportEmails = () => {
    const csv = ['Email,Source,Subscribed At', ...subscribers.map(s => `${s.email},${s.source},${s.subscribed_at}`)].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'email_subscribers.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const handleSavePopup = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/popup-banner`, popup, { headers: { Authorization: `Bearer ${token}` } });
      setPopup(res.data);
      toast.success('Popup banner settings saved!');
    } catch { toast.error('Failed to save popup settings'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-40"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;

  return (
    <div className="space-y-5" data-testid="admin-marketing">
      {/* Sub-tabs */}
      <div className="flex gap-1 bg-gray-200 p-1 rounded w-fit flex-wrap">
        <button onClick={() => setSubTab('banner')}
          className={`px-4 py-2 text-sm font-medium rounded transition-colors ${subTab === 'banner' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
          data-testid="marketing-subtab-banner">
          Site Banner
        </button>
        <button onClick={() => setSubTab('popup')}
          className={`px-4 py-2 text-sm font-medium rounded transition-colors ${subTab === 'popup' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
          data-testid="marketing-subtab-popup">
          Popup Banner
        </button>
        <button onClick={() => setSubTab('emails')}
          className={`px-4 py-2 text-sm font-medium rounded transition-colors ${subTab === 'emails' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
          data-testid="marketing-subtab-emails">
          Email Subscribers ({subTotal})
        </button>
      </div>

      {/* Banner Editor */}
      {subTab === 'banner' && (
        <div className="space-y-4">
          {/* Preview */}
          <div className="bg-white border border-gray-200 rounded overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-100">
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Banner Preview</h3>
            </div>
            <div className="p-5">
              {banner.enabled && banner.text ? (
                <div className="rounded overflow-hidden" style={{ backgroundColor: banner.bg_color, color: banner.text_color }}>
                  <p className="text-center py-2 text-xs tracking-wider font-medium px-4">{banner.text}</p>
                </div>
              ) : (
                <div className="text-center py-4 text-gray-400 text-sm border border-dashed border-gray-200 rounded">
                  Banner is currently disabled
                </div>
              )}
            </div>
          </div>

          {/* Editor */}
          <div className="bg-white border border-gray-200 rounded p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Banner Settings</h3>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={banner.enabled}
                  onChange={(e) => setBanner({ ...banner, enabled: e.target.checked })}
                  className="w-4 h-4 rounded border-gray-300 accent-gray-900" data-testid="banner-enabled" />
                <span className="text-sm text-gray-700 font-medium">Enabled</span>
              </label>
            </div>

            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Banner Text</label>
              <input type="text" value={banner.text}
                onChange={(e) => setBanner({ ...banner, text: e.target.value })}
                placeholder="e.g. FREE SHIPPING ON ALL ORDERS OVER $50"
                className="w-full px-3 py-2.5 border border-gray-200 rounded text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                data-testid="banner-text-input" />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Background Color</label>
                <div className="flex items-center gap-2">
                  <input type="color" value={banner.bg_color}
                    onChange={(e) => setBanner({ ...banner, bg_color: e.target.value })}
                    className="w-10 h-10 rounded border border-gray-200 cursor-pointer" data-testid="banner-bg-color" />
                  <input type="text" value={banner.bg_color}
                    onChange={(e) => setBanner({ ...banner, bg_color: e.target.value })}
                    className="flex-1 px-3 py-2 border border-gray-200 rounded text-sm text-gray-900 font-mono focus:outline-none focus:ring-2 focus:ring-gray-900" />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Text Color</label>
                <div className="flex items-center gap-2">
                  <input type="color" value={banner.text_color}
                    onChange={(e) => setBanner({ ...banner, text_color: e.target.value })}
                    className="w-10 h-10 rounded border border-gray-200 cursor-pointer" data-testid="banner-text-color" />
                  <input type="text" value={banner.text_color}
                    onChange={(e) => setBanner({ ...banner, text_color: e.target.value })}
                    className="flex-1 px-3 py-2 border border-gray-200 rounded text-sm text-gray-900 font-mono focus:outline-none focus:ring-2 focus:ring-gray-900" />
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <button onClick={handleSaveBanner} disabled={saving}
                className="px-6 py-2.5 bg-gray-900 text-white text-sm font-semibold rounded hover:bg-gray-700 disabled:opacity-40 flex items-center gap-2"
                data-testid="save-banner-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Megaphone className="w-4 h-4" />}
                Save Banner
              </button>
              <button onClick={handleRemoveBanner} disabled={saving}
                className="px-6 py-2.5 border border-red-200 text-red-600 text-sm font-medium rounded hover:bg-red-50 disabled:opacity-40"
                data-testid="remove-banner-btn">
                Remove Banner
              </button>
            </div>
          </div>

          {/* Quick Templates */}
          <div className="bg-white border border-gray-200 rounded p-5">
            <h3 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wider">Quick Templates</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              {[
                { text: 'FREE SHIPPING ON ALL ORDERS OVER $50', bg: '#111111', tc: '#ffffff' },
                { text: 'SUMMER SALE — 20% OFF EVERYTHING', bg: '#dc2626', tc: '#ffffff' },
                { text: 'NEW ARRIVALS EVERY WEEK — SHOP NOW', bg: '#1e40af', tc: '#ffffff' },
              ].map((t, i) => (
                <button key={i} onClick={() => setBanner({ enabled: true, text: t.text, bg_color: t.bg, text_color: t.tc })}
                  className="p-3 rounded border border-gray-200 hover:border-gray-400 text-left transition-colors"
                  data-testid={`banner-template-${i}`}>
                  <div className="rounded px-2 py-1 mb-1.5 text-center" style={{ backgroundColor: t.bg, color: t.tc }}>
                    <p className="text-[10px] tracking-wider font-medium truncate">{t.text}</p>
                  </div>
                  <p className="text-[10px] text-gray-400">Click to apply</p>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Email Subscribers */}
      {subTab === 'popup' && (
        <div className="space-y-4">
          {/* Popup Preview */}
          <div className="bg-white border border-gray-200 rounded overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-100">
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Popup Preview</h3>
            </div>
            <div className="p-5 flex justify-center">
              <div className="w-80 bg-gray-950 border border-gray-800 rounded-2xl overflow-hidden">
                <div className="h-1 bg-gradient-to-r from-red-500 via-orange-500 to-amber-400" />
                <div className="px-5 pt-6 pb-5 text-center">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center mx-auto mb-3">
                    <Mail className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-base font-black text-white mb-0.5">
                    {popup.headline} <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-400 to-orange-400">{popup.discount}</span>
                  </h3>
                  <p className="text-gray-400 text-xs mb-3">{popup.subtext}</p>
                  <div className="flex gap-1.5">
                    <div className="flex-1 px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-gray-500 text-xs text-left">Enter your email</div>
                    <div className="px-3 py-2 bg-gradient-to-r from-red-500 to-orange-500 text-white font-bold text-xs rounded-lg">JOIN</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Popup Settings */}
          <div className="bg-white border border-gray-200 rounded p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Popup Banner Settings</h3>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={popup.enabled}
                  onChange={(e) => setPopup({ ...popup, enabled: e.target.checked })}
                  className="w-4 h-4 rounded border-gray-300 accent-gray-900" data-testid="popup-enabled" />
                <span className="text-sm text-gray-700 font-medium">Enabled</span>
              </label>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Headline</label>
                <input type="text" value={popup.headline}
                  onChange={(e) => setPopup({ ...popup, headline: e.target.value })}
                  placeholder="e.g. GET UP TO"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  data-testid="popup-headline-input" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Discount Text</label>
                <input type="text" value={popup.discount}
                  onChange={(e) => setPopup({ ...popup, discount: e.target.value })}
                  placeholder="e.g. 15% OFF"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  data-testid="popup-discount-input" />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Description</label>
              <input type="text" value={popup.subtext}
                onChange={(e) => setPopup({ ...popup, subtext: e.target.value })}
                placeholder="Sign up message..."
                className="w-full px-3 py-2.5 border border-gray-200 rounded text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                data-testid="popup-subtext-input" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Delay (seconds before popup appears)</label>
              <input type="number" min="0" max="60" value={popup.delay_seconds}
                onChange={(e) => setPopup({ ...popup, delay_seconds: parseInt(e.target.value) || 0 })}
                className="w-24 px-3 py-2.5 border border-gray-200 rounded text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                data-testid="popup-delay-input" />
            </div>
            <div className="pt-2">
              <button onClick={handleSavePopup} disabled={saving}
                className="px-6 py-2.5 bg-gray-900 text-white text-sm font-semibold rounded hover:bg-gray-700 disabled:opacity-40 flex items-center gap-2"
                data-testid="save-popup-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Megaphone className="w-4 h-4" />}
                Save Popup Settings
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email Subscribers */}
      {subTab === 'emails' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Email Subscribers</h2>
              <p className="text-sm text-gray-500">{subTotal} subscriber{subTotal !== 1 ? 's' : ''} captured</p>
            </div>
            {subscribers.length > 0 && (
              <button onClick={handleExportEmails}
                className="px-4 py-2 bg-gray-900 text-white text-sm font-semibold rounded hover:bg-gray-700 flex items-center gap-2"
                data-testid="export-emails-btn">
                <Mail className="w-4 h-4" /> Export CSV
              </button>
            )}
          </div>

          {subscribers.length === 0 ? (
            <div className="bg-white border border-gray-200 rounded p-10 text-center">
              <Mail className="w-10 h-10 mx-auto text-gray-300 mb-3" />
              <h3 className="text-lg font-bold text-gray-900 mb-1">No Subscribers Yet</h3>
              <p className="text-sm text-gray-500">Emails will appear here when visitors subscribe on the site.</p>
            </div>
          ) : (
            <div className="bg-white border border-gray-200 rounded overflow-hidden">
              <table className="w-full" data-testid="subscribers-table">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Email</th>
                    <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Source</th>
                    <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="text-right p-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {subscribers.map((sub) => (
                    <tr key={sub.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`subscriber-${sub.id}`}>
                      <td className="p-3 text-sm text-gray-900 font-medium">{sub.email}</td>
                      <td className="p-3 text-sm text-gray-500 capitalize">{sub.source}</td>
                      <td className="p-3 text-sm text-gray-400">{new Date(sub.subscribed_at).toLocaleDateString()}</td>
                      <td className="p-3 text-right">
                        <button onClick={() => handleDeleteSubscriber(sub.id)}
                          className="p-1.5 rounded text-gray-400 hover:text-red-600 hover:bg-red-50"
                          data-testid={`delete-subscriber-${sub.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};


export default MarketingTab;
